/**
 * The one screen in the headset, and how each eye gets its own half of it.
 *
 * three renders an XR frame twice and marks the cameras by layer -- layer 1 is the left eye,
 * layer 2 the right (WebXRManager: `cameraXR.layers.mask = camera.layers.mask | 0b110`). So a
 * side-by-side frame becomes two meshes in the same place, each on one eye's layer, sampling
 * its own half. Plain WebGL and nothing clever, which is what a view an operator relies on
 * should be.
 */
import { DoubleSide, Group, Mesh, MeshBasicMaterial, PlaneGeometry, type Texture } from "three";

export type ScreenOptions = {
  /** Metres wide, at `distance` metres ahead. The D405 pair's 87 degrees fills a natural view here. */
  width?: number;
  distance?: number;
  /** Source pixels to slide the right eye by: where zero disparity, and so the screen plane, sits. */
  convergence?: number;
  swapEyes?: boolean;
  /** Show a stereo pair as one picture on both eyes instead of one half each.
   *  Depth is easier to judge, but a pair that is badly aligned, or a scene too dark to fuse,
   *  is a headache rather than a view -- and then one eye's half, flat, is the better picture. */
  flatten?: boolean;
  /** Source pixels to lift the right eye by. A pair that is out of line vertically is the
   *  usual reason eyes refuse to fuse one, and no amount of separation fixes it. */
  verticalOffset?: number;
};

const DEFAULTS = { width: 1.5, distance: 1.0, convergence: 0, swapEyes: false, flatten: false,
                   verticalOffset: 0 };

export class StereoScreen {
  readonly group = new Group();
  private readonly left: Mesh;
  private readonly right: Mesh;
  private options = { ...DEFAULTS };
  private stereo = false;
  private frameWidth = 1280;

  constructor(texture: Texture) {
    const material = new MeshBasicMaterial({ map: texture, side: DoubleSide, toneMapped: false });
    this.left = new Mesh(new PlaneGeometry(1, 1), material);
    this.right = new Mesh(new PlaneGeometry(1, 1), material);
    this.left.layers.set(1);
    this.right.layers.set(2);
    this.left.renderOrder = -1;
    this.right.renderOrder = -1;
    this.group.add(this.left, this.right);
    this.setSource(1280, 720, false);
    this.group.position.set(0, 0, -DEFAULTS.distance);   // until the first viewer pose places it
  }

  set(options: ScreenOptions): void {
    this.options = { ...this.options, ...options };
    this.setSource(this.frameWidth, 0, this.stereo, true);
  }

  /** Called when the first frame arrives, and whenever its shape changes. */
  setSource(frameWidth: number, frameHeight: number, stereo: boolean, keepShape = false): void {
    this.stereo = stereo;
    this.frameWidth = frameWidth || this.frameWidth;
    const { width, convergence, swapEyes, flatten, verticalOffset } = this.options;
    const eyeWidth = stereo ? this.frameWidth / 2 : this.frameWidth;
    if (!keepShape && frameHeight > 0) {
      const height = width * (frameHeight / eyeWidth);
      for (const mesh of [this.left, this.right]) {
        mesh.geometry.dispose();
        mesh.geometry = new PlaneGeometry(width, height);
      }
    }
    const shift = stereo && !flatten ? convergence / this.frameWidth : 0;
    // u runs 0..0.5 for one eye and 0.5..1 for the other; the shift slides the right eye's
    // window, which is what moves the scene in front of or behind the screen. Flattened, both
    // eyes are given the same half -- one whole picture, no disparity, nothing to fuse.
    const one: [number, number] = swapEyes ? [0.5, 0.5] : [0, 0.5];
    const halves: [number, number][] = !stereo
      ? [[0, 1], [0, 1]]
      : flatten
        ? [one, one]
        : (swapEyes ? [[0.5 + shift, 0.5], [0, 0.5]] : [[0, 0.5], [0.5 + shift, 0.5]]);
    const lift = stereo && !flatten && frameHeight > 0 ? verticalOffset / frameHeight : 0;
    this.uvs(this.left, halves[0], 0);
    this.uvs(this.right, halves[1], lift);
  }

  private uvs(mesh: Mesh, [offset, span]: [number, number], lift: number): void {
    // Written from the plane's own corner values every time, never adjusted in place: this
    // runs on each arriving frame, and nudging what is already there would walk the picture
    // off the screen within a second.
    const uv = mesh.geometry.attributes.uv;
    for (let i = 0; i < uv.count; i += 1) {
      const u = i % 2 === 0 ? 0 : 1;      // PlaneGeometry's corners: u is 0 or 1 ...
      const v = i < 2 ? 1 : 0;            // ... and v is 1 on the top row, 0 on the bottom
      uv.setXY(i, offset + u * span, v + lift);
    }
    uv.needsUpdate = true;
  }

  /** The picture's size in metres, for what is hung on its edges. */
  size(): { width: number; height: number } {
    const g = this.left.geometry as PlaneGeometry;
    return { width: g.parameters.width, height: g.parameters.height };
  }

  /** Put the screen where the operator is looking, level, and one distance ahead. */
  place(position: { x: number; y: number; z: number }, yaw: number): void {
    const { distance } = this.options;
    this.group.position.set(position.x - Math.sin(yaw) * distance, position.y - 0.05,
                            position.z - Math.cos(yaw) * distance);
    this.group.rotation.set(0, yaw, 0);
  }

  dispose(): void {
    for (const mesh of [this.left, this.right]) mesh.geometry.dispose();
    (this.left.material as MeshBasicMaterial).dispose();
  }
}
